import {
  Alert,
  Box,
  Button,
  ButtonBase,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Drawer,
  FormControlLabel,
  IconButton,
  Menu,
  MenuItem,
  Stack,
  SvgIcon,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import { EditorContent, ReactRenderer, useEditor } from '@tiptap/react';

import { BubbleMenu } from '@tiptap/react/menus';
import Mention from '@tiptap/extension-mention';
import Link from '@tiptap/extension-link';
import Placeholder from '@tiptap/extension-placeholder';
import { Table, TableRow, TableCell, TableHeader } from '@tiptap/extension-table';
import CodeBlockLowlight from '@tiptap/extension-code-block-lowlight';
import { common, createLowlight } from 'lowlight';
import StarterKit from '@tiptap/starter-kit';
import { Markdown } from 'tiptap-markdown';
import AgentInstruction from 'services/api/AgentInstruction';
import Agent from 'services/api/Agent';
import AlertOctagonOutlineIcon from 'mdi-react/AlertOctagonOutlineIcon';
import CloseIcon from 'mdi-react/CloseIcon';
import ContentCopyIcon from 'mdi-react/ContentCopyIcon';
import DotsVerticalIcon from 'mdi-react/DotsVerticalIcon';
import EyeOutlineIcon from 'mdi-react/EyeOutlineIcon';
import HistoryIcon from 'mdi-react/HistoryIcon';
import TrayArrowDownIcon from 'mdi-react/TrayArrowDownIcon';
import TrayArrowUpIcon from 'mdi-react/TrayArrowUpIcon';
import FormatBoldIcon from 'mdi-react/FormatBoldIcon';
import FormatHeader1Icon from 'mdi-react/FormatHeader1Icon';
import FormatHeader2Icon from 'mdi-react/FormatHeader2Icon';
import FormatHeader3Icon from 'mdi-react/FormatHeader3Icon';
import FormatItalicIcon from 'mdi-react/FormatItalicIcon';
import FormatListBulletedIcon from 'mdi-react/FormatListBulletedIcon';
import FormatListNumberedIcon from 'mdi-react/FormatListNumberedIcon';
import CodeTagsIcon from 'mdi-react/CodeTagsIcon';
import CodeBracesIcon from 'mdi-react/CodeBracesIcon';
import TableIcon from 'mdi-react/TableIcon';
import TableColumnPlusAfterIcon from 'mdi-react/TableColumnPlusAfterIcon';
import TableColumnRemoveIcon from 'mdi-react/TableColumnRemoveIcon';
import TableRowPlusAfterIcon from 'mdi-react/TableRowPlusAfterIcon';
import TableRowRemoveIcon from 'mdi-react/TableRowRemoveIcon';
import TableRemoveIcon from 'mdi-react/TableRemoveIcon';
import UnfoldLessHorizontalIcon from 'mdi-react/UnfoldLessHorizontalIcon';
import UnfoldMoreHorizontalIcon from 'mdi-react/UnfoldMoreHorizontalIcon';
import PropTypes from 'prop-types';
import React, {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react';
import { I18n } from 'utils/i18n';
import ChannelModalityIcon from 'components/@home/@settings/Tabs/Agents/instructionEditor/ChannelModalityIcon';
import MentionList from './instructionEditor/MentionList';
import { localizeReferenceDescription } from './instructionEditor/referenceLabels';
import CollapsibleHeadings, {
  collapsedHeadingsKey,
  collectCollapsibleHeadingKeys,
} from './instructionEditor/CollapsibleHeadings';
import BlockScope, { blockScopeKey, normaliseScopeAttr } from './instructionEditor/BlockScope';
import {
  MODALITIES,
  extractScopesFromMarkdown,
  injectScopesIntoMarkdown,
  toggleScopeModality,
  scopeIncludes,
} from './instructionEditor/scopeMarkdown';
import VariableHighlight from './instructionEditor/VariableHighlight';

const lowlight = createLowlight(common);

const MarkdownMention = Mention.extend({
  addStorage() {
    return {
      ...(this.parent?.() || {}),
      markdown: {
        serialize(state, node) {
          state.write(`@${node.attrs.id}`);
        },
        parse: {},
      },
    };
  },
});

const getStyles = theme => {
  const isDark = theme.palette.mode === 'dark';
  const softPanel = theme.palette.action.subtleLight;
  const codeBg = theme.palette.action.subtle;
  const warningSoftBg = alpha(theme.palette.warning.main, isDark ? 0.22 : 0.16);
  const warningText = isDark ? theme.palette.warning.light : theme.palette.warning.dark;
  return {
    root: {
      backgroundColor: theme.palette.background.paper,
      display: 'flex',
      flexDirection: 'column',
      flex: 1,
      minHeight: 0,
      overflow: 'hidden',
    },
    titleStrip: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      gap: theme.spacing(1),
      padding: theme.spacing(1.25, 2),
      borderBottom: `1px solid ${theme.palette.divider}`,
      backgroundColor: theme.palette.background.paper,
      flexShrink: 0,
    },
    toolbarStrip: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      gap: theme.spacing(1),
      padding: theme.spacing(0.75, 2),
      backgroundColor: theme.palette.background.paper,
      flexShrink: 0,
    },
    headerTitle: {
      fontSize: theme.typography.pxToRem(16),
      fontWeight: 700,
      color: theme.palette.text.primary,
      letterSpacing: '-0.2px',
    },
    helper: {
      fontSize: theme.typography.pxToRem(12.5),
      color: theme.palette.text.secondary,
      padding: theme.spacing(0.5, 2, 1, 2),
      borderBottom: `1px solid ${theme.palette.divider}`,
      flexShrink: 0,
    },
    meta: {
      fontSize: theme.typography.pxToRem(12),
      color: theme.palette.text.secondary,
    },
    channelsSwitchLabel: {
      margin: 0,
      gap: theme.spacing(0.75),
      '& .MuiFormControlLabel-label': {
        fontSize: theme.typography.pxToRem(12.5),
        fontWeight: 500,
        color: theme.palette.text.primary,
      },
    },
    iconButton: {
      minWidth: 0,
      border: `1px solid ${theme.palette.divider}`,
      borderRadius: '6px',
      padding: theme.spacing(0.5, 1),
      fontSize: theme.typography.pxToRem(12.5),
      fontWeight: 500,
      color: theme.palette.text.primary,
      backgroundColor: theme.palette.background.paper,
      cursor: 'pointer',
      display: 'inline-flex',
      alignItems: 'center',
      gap: theme.spacing(0.5),
      '&:hover': { backgroundColor: theme.palette.action.hover },
      '&:disabled': { color: theme.palette.text.disabled, cursor: 'default' },
    },
    editorWrap: {
      position: 'relative',
      flex: 1,
      minHeight: 0,
      overflowY: 'auto',
      padding: theme.spacing(0, 5),
      backgroundColor: theme.palette.background.paper,
      '& .ProseMirror': {
        outline: 'none',
        minHeight: 120,
        fontSize: theme.typography.pxToRem(14),
        lineHeight: 1.6,
        color: theme.palette.text.primary,
        caretColor: theme.palette.primary.main,
      },
      '& .ProseMirror p': { margin: theme.spacing(0.5, 0) },
      '& .ProseMirror h1': {
        fontSize: theme.typography.pxToRem(20),
        fontWeight: 700,
        margin: theme.spacing(2.5, 0, 1),
        letterSpacing: '-0.3px',
      },
      '& .ProseMirror h2': {
        fontSize: theme.typography.pxToRem(16),
        fontWeight: 700,
        margin: theme.spacing(2.25, 0, 0.75),
        color: theme.palette.text.primary,
      },
      '& .ProseMirror h3': {
        fontSize: theme.typography.pxToRem(14),
        fontWeight: 700,
        margin: theme.spacing(2, 0, 0.75),
        color: theme.palette.text.primary,
        textTransform: 'uppercase',
        letterSpacing: '0.4px',
      },
      '& .ProseMirror ul, & .ProseMirror ol': {
        paddingLeft: theme.spacing(3),
        margin: theme.spacing(0.5, 0),
      },
      '& .ProseMirror li > p': { margin: theme.spacing(0.25, 0) },
      '& .ProseMirror code': {
        fontFamily: 'ui-monospace, "SF Mono", Menlo, monospace',
        fontSize: '0.9em',
        background: codeBg,
        padding: '1px 5px',
        borderRadius: '4px',
      },
      '& .ProseMirror pre': {
        background: softPanel,
        color: theme.palette.text.primary,
        padding: theme.spacing(1.5),
        borderRadius: '6px',
        border: `1px solid ${theme.palette.divider}`,
        fontFamily: 'ui-monospace, "SF Mono", Menlo, monospace',
        fontSize: theme.typography.pxToRem(12.5),
        overflowX: 'auto',
        margin: theme.spacing(1, 0),
      },
      '& .ProseMirror pre code': {
        background: 'transparent',
        color: 'inherit',
        padding: 0,
        border: 0,
        fontSize: 'inherit',
      },
      '& .ProseMirror .hljs-comment, & .ProseMirror .hljs-quote': {
        color: '#6A737D',
        fontStyle: 'italic',
      },
      '& .ProseMirror .hljs-keyword, & .ProseMirror .hljs-selector-tag, & .ProseMirror .hljs-literal, & .ProseMirror .hljs-section, & .ProseMirror .hljs-link':
        { color: '#D73A49' },
      '& .ProseMirror .hljs-string, & .ProseMirror .hljs-attribute, & .ProseMirror .hljs-addition, & .ProseMirror .hljs-template-tag, & .ProseMirror .hljs-template-variable':
        { color: '#22863A' },
      '& .ProseMirror .hljs-title, & .ProseMirror .hljs-name, & .ProseMirror .hljs-built_in, & .ProseMirror .hljs-type, & .ProseMirror .hljs-symbol, & .ProseMirror .hljs-bullet, & .ProseMirror .hljs-variable':
        { color: '#6F42C1' },
      '& .ProseMirror .hljs-number, & .ProseMirror .hljs-meta': { color: '#005CC5' },
      '& .ProseMirror .hljs-tag, & .ProseMirror .hljs-selector-id, & .ProseMirror .hljs-selector-class, & .ProseMirror .hljs-regexp, & .ProseMirror .hljs-deletion':
        { color: '#B31D28' },
      '& .ProseMirror .hljs-params': { color: '#E36209' },
      '& .ProseMirror .hljs-emphasis': { fontStyle: 'italic' },
      '& .ProseMirror .hljs-strong': { fontWeight: 700 },
      '& .ProseMirror blockquote': {
        borderLeft: `3px solid ${theme.palette.divider}`,
        paddingLeft: theme.spacing(1.5),
        color: theme.palette.text.secondary,
        margin: theme.spacing(1, 0),
      },
      '& .ProseMirror table': {
        borderCollapse: 'collapse',
        tableLayout: 'auto',
        width: 'auto',
        margin: theme.spacing(1.5, 0),
      },
      '& .ProseMirror table td, & .ProseMirror table th': {
        border: 'none',
        borderBottom: `1px solid ${theme.palette.divider}`,
        padding: theme.spacing(1, 1.5),
        verticalAlign: 'top',
        position: 'relative',
        minWidth: 60,
      },
      '& .ProseMirror table tr:last-child td, & .ProseMirror table tr:last-child th': {
        borderBottom: 'none',
      },
      '& .ProseMirror table th': {
        fontWeight: 600,
        textAlign: 'left',
        borderBottom: `1.5px solid ${theme.palette.text.primary}`,
        color: theme.palette.text.primary,
      },
      '& .ProseMirror table p': {
        margin: 0,
      },
      '& .ProseMirror table .selectedCell:after': {
        content: '""',
        position: 'absolute',
        inset: 0,
        backgroundColor: alpha(theme.palette.primary.main, 0.12),
        pointerEvents: 'none',
      },
      '& .ProseMirror table .column-resize-handle': {
        position: 'absolute',
        right: '-2px',
        top: 0,
        bottom: 0,
        width: 4,
        backgroundColor: theme.palette.primary.main,
        pointerEvents: 'none',
      },
      '& .ProseMirror .tableWrapper': {
        overflowX: 'auto',
      },
      '& .ProseMirror.resize-cursor': {
        cursor: 'col-resize',
      },
      '& .tiptap-mention': {
        display: 'inline-block',
        background: theme.palette.mentionBack,
        color: theme.palette.mention,
        borderRadius: '4px',
        padding: '1px 6px',
        fontSize: '0.92em',
        fontWeight: 600,
        cursor: 'default',
      },
      '& .tiptap-variable': {
        display: 'inline',
        background: warningSoftBg,
        color: warningText,
        borderRadius: '4px',
        padding: '0 4px',
        fontFamily: 'ui-monospace, "SF Mono", Menlo, monospace',
        fontSize: '0.92em',
        fontWeight: 500,
      },
      '& .tiptap-link': {
        color: theme.palette.primary.main,
        textDecoration: 'underline',
        cursor: 'pointer',
      },
      '& .ProseMirror p.is-editor-empty:first-of-type::before': {
        content: 'attr(data-placeholder)',
        float: 'left',
        color: theme.palette.text.disabled,
        pointerEvents: 'none',
        height: 0,
      },
      '& .tiptap-heading': {
        position: 'relative',
      },
      '& .tiptap-heading::before': {
        content: '""',
        position: 'absolute',
        left: theme.spacing(-2.75),
        top: '0.35em',
        width: 18,
        height: 18,
        backgroundImage: `url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='%239CA3AF'><path d='M7.41,8.58L12,13.17L16.58,8.58L18,10L12,16L6,10L7.41,8.58Z'/></svg>")`,
        backgroundRepeat: 'no-repeat',
        backgroundSize: 'contain',
        transition: 'transform 120ms ease',
        transformOrigin: 'center',
        cursor: 'pointer',
      },
      '& .tiptap-heading.tiptap-heading-collapsed::before': {
        transform: 'rotate(-90deg)',
      },
      '& .tiptap-heading-l3::before': { display: 'none' },
      '& .tiptap-collapsed-block': { display: 'none' },
      '& .ProseMirror > p, & .ProseMirror > h1, & .ProseMirror > h2, & .ProseMirror > h3': {
        paddingLeft: '10px',
        paddingRight: '10px',
        marginLeft: '-10px',
        marginRight: '-10px',
      },
      '& .tiptap-scoped-block': {
        background: softPanel,
        borderRadius: '6px',
        boxShadow: `0 4px 0 ${softPanel}, 0 -4px 0 ${softPanel}`,
      },
      '& .tiptap-scope-chips': {
        float: 'right',
        marginLeft: theme.spacing(1.5),
        display: 'inline-flex',
        alignItems: 'center',
        gap: theme.spacing(1),
        userSelect: 'none',
        pointerEvents: 'auto',
        marginTop: theme.spacing(0.5),
        marginRight: theme.spacing(1),
      },
      '& .tiptap-scope-icon': {
        appearance: 'none',
        background: 'transparent',
        border: 0,
        padding: 0,
        color: theme.palette.text.disabled,
        cursor: 'pointer',
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        transition: 'color 100ms ease',
        '&:hover': { color: theme.palette.text.secondary },
        '&.is-active': { color: theme.palette.text.primary },
        '&.is-active:hover': { color: theme.palette.text.primary },
      },
    },
    bubble: {
      display: 'flex',
      alignItems: 'center',
      gap: '2px',
      background: theme.palette.text.primary,
      borderRadius: '6px',
      padding: '4px',
      boxShadow: theme.shadows[6],
    },
    bubbleBtn: {
      background: 'transparent',
      border: 0,
      color: theme.palette.background.paper,
      borderRadius: '4px',
      width: 28,
      height: 28,
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
      cursor: 'pointer',
      '&:hover': { background: alpha(theme.palette.background.paper, 0.12) },
      '&[data-active="true"]': { background: theme.palette.primary.main },
    },
    bubbleDivider: {
      width: '1px',
      height: 18,
      backgroundColor: alpha(theme.palette.background.paper, 0.2),
      margin: '0 4px',
    },
    detected: {
      display: 'flex',
      alignItems: 'center',
      flexWrap: 'wrap',
      gap: theme.spacing(0.5),
      padding: theme.spacing(1, 1.5),
      borderTop: `1px solid ${theme.palette.divider}`,
      backgroundColor: theme.palette.background.subtle,
      minHeight: 36,
      flexShrink: 0,
    },
    detectedLabel: {
      fontSize: theme.typography.pxToRem(10.5),
      fontWeight: 700,
      letterSpacing: '0.5px',
      textTransform: 'uppercase',
      color: theme.palette.text.secondary,
      marginRight: theme.spacing(1),
    },
    mentionChipResolved: {
      height: 22,
      fontFamily: 'ui-monospace, "SF Mono", Menlo, monospace',
      fontSize: theme.typography.pxToRem(11.5),
      backgroundColor: theme.palette.mentionBack,
      color: theme.palette.mention,
      border: `1px solid ${alpha(theme.palette.mention, 0.4)}`,
    },
    mentionChipUnresolved: {
      height: 22,
      fontFamily: 'ui-monospace, "SF Mono", Menlo, monospace',
      fontSize: theme.typography.pxToRem(11.5),
      backgroundColor: warningSoftBg,
      color: warningText,
      border: `1px solid ${alpha(theme.palette.warning.main, 0.4)}`,
    },
    dirtyChip: {
      height: 20,
      fontSize: theme.typography.pxToRem(10.5),
      fontWeight: 600,
      letterSpacing: '0.3px',
      textTransform: 'uppercase',
      backgroundColor: warningSoftBg,
      color: warningText,
    },
    versionsDialogList: {
      padding: theme.spacing(0, 1),
      maxHeight: '70vh',
      overflowY: 'auto',
    },
    versionRow: {
      display: 'flex',
      alignItems: 'flex-start',
      justifyContent: 'space-between',
      gap: theme.spacing(2),
      padding: theme.spacing(1.5, 1),
      borderBottom: `1px solid ${theme.palette.divider}`,
      '&:last-child': { borderBottom: 'none' },
    },
    versionMain: {
      minWidth: 0,
      flex: 1,
    },
    versionTitleRow: {
      display: 'flex',
      alignItems: 'center',
      gap: theme.spacing(1),
      flexWrap: 'wrap',
    },
    versionTitle: {
      fontSize: theme.typography.pxToRem(13.5),
      fontWeight: 600,
      color: theme.palette.text.primary,
    },
    versionMeta: {
      fontSize: theme.typography.pxToRem(12),
      color: theme.palette.text.secondary,
    },
    versionCommit: {
      fontSize: theme.typography.pxToRem(12.5),
      color: theme.palette.text.primary,
    },
    rollbackButton: {
      color: theme.palette.error.main,
      textTransform: 'none',
      fontSize: theme.typography.pxToRem(12.5),
      fontWeight: 500,
      minWidth: 0,
      padding: theme.spacing(0.25, 1),
      '&:hover': { backgroundColor: alpha(theme.palette.error.main, 0.08) },
    },
    currentTag: {
      fontSize: theme.typography.pxToRem(12),
      fontWeight: 600,
      color: theme.palette.success.main,
    },
    previewDrawer: {
      '& .MuiDrawer-paper': {
        width: 'min(720px, 95vw)',
        padding: 0,
      },
    },
    previewHeader: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      padding: theme.spacing(2, 2.5),
      borderBottom: `1px solid ${theme.palette.divider}`,
    },
    previewTitle: {
      fontSize: theme.typography.pxToRem(16),
      fontWeight: 700,
      color: theme.palette.text.primary,
    },
    previewBody: {
      padding: theme.spacing(2.5),
      fontFamily: 'ui-monospace, "SF Mono", Menlo, monospace',
      fontSize: theme.typography.pxToRem(13),
      lineHeight: 1.65,
      whiteSpace: 'pre-wrap',
      color: theme.palette.text.primary,
      overflowY: 'auto',
      height: 'calc(100vh - 70px)',
    },
    previewLine: {
      display: 'block',
    },
    previewHeading: {
      display: 'block',
      color: theme.palette.text.secondary,
      fontWeight: 500,
    },
    previewBody2: {
      display: 'block',
      paddingLeft: theme.spacing(2),
      borderLeft: `2px solid ${theme.palette.divider}`,
      marginLeft: theme.spacing(1.5),
    },
  };
};

const MENTION_REGEX = /(^|[^a-zA-Z0-9_])@([a-zA-Z][a-zA-Z0-9_-]*)/g;

const extractMentionsFromMarkdown = md => {
  if (!md) return [];
  const re = new RegExp(MENTION_REGEX.source, 'g');
  const seen = new Set();
  const out = [];
  [...md.matchAll(re)].forEach(match => {
    const name = match[2];
    if (seen.has(name)) return;
    seen.add(name);
    out.push({ name });
  });
  return out;
};

const blocksToText = blocks => {
  if (!blocks?.length) return '';
  return blocks
    .map(b => {
      if (!b) return '';
      if (b.type === 'h1') return `# ${b.text || b.title || ''}`;
      if (b.type === 'h2') return `## ${b.text || b.title || ''}`;
      if (b.type === 'section') return b.title ? `### ${b.title}\n${b.text || ''}` : b.text || '';
      return b.text || '';
    })
    .filter(Boolean)
    .join('\n\n');
};

const formatDate = value => (value ? new Date(value).toLocaleString() : '');

/**
 * After loading markdown into Tiptap, walk the doc and convert plain-text `@name`
 * tokens into mention nodes when `name` is in the known set.
 */
const decoratePlainMentions = (editor, knownNames) => {
  if (!editor || !knownNames || knownNames.size === 0) return;
  const mentionType = editor.schema.nodes.mention;
  if (!mentionType) return;
  const { tr } = editor.state;
  const replacements = [];
  editor.state.doc.descendants((node, pos) => {
    if (!node.isText) return;
    const re = new RegExp(MENTION_REGEX.source, 'g');
    [...node.text.matchAll(re)].forEach(match => {
      const name = match[2];
      if (!knownNames.has(name)) return;
      const start = pos + match.index + match[1].length;
      replacements.push({ from: start, to: start + 1 + name.length, id: name });
    });
  });
  if (replacements.length === 0) return;
  replacements
    .sort((a, b) => b.from - a.from)
    .forEach(r => {
      const mentionNode = mentionType.create({ id: r.id, label: r.id });
      tr.replaceWith(r.from, r.to, mentionNode);
    });
  if (tr.steps.length) editor.view.dispatch(tr);
};

const isListContainer = name => name === 'bulletList' || name === 'orderedList';

const collectScopesFromEditor = editor => {
  if (!editor) return [];
  const scopes = [];
  editor.state.doc.forEach(node => {
    const blockScope = normaliseScopeAttr(node.attrs.scope);
    if (isListContainer(node.type.name)) {
      const itemScopes = [];
      node.forEach(item => {
        if (item.type.name === 'listItem') {
          itemScopes.push(normaliseScopeAttr(item.attrs.scope));
        }
      });
      scopes.push({ scope: blockScope, itemScopes });
    } else {
      scopes.push({ scope: blockScope });
    }
  });
  return scopes;
};

const applyScopesToEditor = (editor, scopes) => {
  if (!editor || !scopes?.length) return;
  const { state, view } = editor;
  let { tr } = state;
  let mutated = false;
  let i = 0;
  state.doc.forEach((node, pos) => {
    const entry = scopes[i];
    i += 1;
    if (!entry) return;
    const blockScope = Array.isArray(entry) ? entry : entry.scope;
    if (blockScope) {
      tr = tr.setNodeMarkup(pos, undefined, { ...node.attrs, scope: blockScope });
      mutated = true;
    }
    const itemScopes = !Array.isArray(entry) ? entry.itemScopes : null;
    if (itemScopes && isListContainer(node.type.name)) {
      let liOffset = 0;
      let liIndex = 0;
      node.forEach(item => {
        const itemPos = pos + 1 + liOffset;
        liOffset += item.nodeSize;
        if (item.type.name !== 'listItem') return;
        const itemScope = itemScopes[liIndex];
        liIndex += 1;
        if (!itemScope) return;
        tr = tr.setNodeMarkup(itemPos, undefined, { ...item.attrs, scope: itemScope });
        mutated = true;
      });
    }
  });
  if (mutated) view.dispatch(tr);
};

const filterMarkdownForPreview = (md, modality) => {
  if (!md) return '';
  const { stripped, scopes } = extractScopesFromMarkdown(md);
  const blocks = stripped.split(/\n{2,}/);
  return blocks
    .filter((_, idx) => {
      const entry = scopes[idx];
      const scope = entry && !Array.isArray(entry) ? entry.scope : entry;
      return scopeIncludes(scope, modality);
    })
    .join('\n\n');
};

const renderPreviewLine = (line, styles, key) => {
  if (/^#{1,6}\s+/.test(line)) {
    return (
      <Box key={key} component="span" sx={styles.previewHeading}>
        {line}
      </Box>
    );
  }
  if (line === '') {
    return (
      <Box key={key} component="span" sx={styles.previewLine}>
        {'\n'}
      </Box>
    );
  }
  return (
    <Box key={key} component="span" sx={styles.previewBody2}>
      {line}
    </Box>
  );
};

const InstructionEditor = forwardRef(
  (
    {
      entityType,
      entityId = null,
      title = null,
      helper = null,
      availableTools = [],
      availableSkills = [],
      parentManagedSave = false,
      onDirtyChange = null,
      onSaved = null,
    },
    ref,
  ) => {
    const theme = useTheme();
    const styles = useMemo(() => getStyles(theme), [theme]);
    const [versions, setVersions] = useState([]);
    const [loadedMarkdown, setLoadedMarkdown] = useState('');
    const [currentMarkdown, setCurrentMarkdown] = useState('');
    const [historyOpen, setHistoryOpen] = useState(false);
    const [menuAnchor, setMenuAnchor] = useState(null);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [commit, setCommit] = useState('');
    const [saving, setSaving] = useState(false);
    const [builtinTools, setBuiltinTools] = useState([]);
    const [collapsed, setCollapsed] = useState(() => new Set());
    const [previewOpen, setPreviewOpen] = useState(false);
    const [copyTooltip, setCopyTooltip] = useState(false);
    const [viewChannels, setViewChannels] = useState(false);
    const [previewModality, setPreviewModality] = useState('text');
    const collapsedRef = useRef(collapsed);
    collapsedRef.current = collapsed;
    const viewChannelsRef = useRef(viewChannels);
    viewChannelsRef.current = viewChannels;
    const editorRef = useRef(null);
    const importInputRef = useRef(null);

    useEffect(() => {
      Agent.listBuiltinTools()
        .then(setBuiltinTools)
        .catch(() => setBuiltinTools([]));
    }, []);

    const referenceCatalog = useMemo(() => {
      const builtins = builtinTools.map(t => {
        const item = { kind: 'builtin', name: t.name, description: t.description };
        return { ...item, description: localizeReferenceDescription(item) };
      });
      const tools = (availableTools || []).map(t => ({
        kind: 'tool',
        name: t.name,
        description: t.description || t.displayName || '',
      }));
      const skills = (availableSkills || []).map(s => ({
        kind: 'skill',
        name: s.name,
        description: s.description || s.displayName || '',
      }));
      const map = new Map();
      [...builtins, ...tools, ...skills].forEach(r => {
        if (!map.has(r.name)) map.set(r.name, r);
      });
      return [...map.values()];
    }, [availableTools, availableSkills, builtinTools]);

    const referenceByName = useMemo(() => {
      const m = new Map();
      referenceCatalog.forEach(r => m.set(r.name, r));
      return m;
    }, [referenceCatalog]);
    const referenceCatalogRef = useRef(referenceCatalog);
    referenceCatalogRef.current = referenceCatalog;

    const renderMentionSuggestion = useCallback(() => {
      let component;
      let popupEl;
      return {
        onStart: suggestionProps => {
          component = new ReactRenderer(MentionList, {
            props: suggestionProps,
            editor: suggestionProps.editor,
          });
          popupEl = document.createElement('div');
          popupEl.style.position = 'absolute';
          popupEl.style.zIndex = 1500;
          document.body.appendChild(popupEl);
          popupEl.appendChild(component.element);
          const rect = suggestionProps.clientRect?.();
          if (rect) {
            popupEl.style.top = `${rect.bottom + window.scrollY + 4}px`;
            popupEl.style.left = `${rect.left + window.scrollX}px`;
          }
        },
        onUpdate(suggestionProps) {
          component?.updateProps(suggestionProps);
          const rect = suggestionProps.clientRect?.();
          if (rect && popupEl) {
            popupEl.style.top = `${rect.bottom + window.scrollY + 4}px`;
            popupEl.style.left = `${rect.left + window.scrollX}px`;
          }
        },
        onKeyDown(suggestionProps) {
          if (suggestionProps.event.key === 'Escape') {
            popupEl?.remove();
            return true;
          }
          return component?.ref?.onKeyDown(suggestionProps) || false;
        },
        onExit() {
          component?.destroy();
          popupEl?.remove();
        },
      };
    }, []);

    const editor = useEditor(
      {
        extensions: [
          StarterKit.configure({
            heading: { levels: [1, 2, 3] },
            codeBlock: false,
            link: false,
            horizontalRule: false,
          }),
          CodeBlockLowlight.configure({
            lowlight,
            defaultLanguage: 'plaintext',
            exitOnTripleEnter: true,
            exitOnArrowDown: true,
            HTMLAttributes: { class: 'tiptap-code-block' },
          }),
          Link.configure({
            openOnClick: true,
            linkOnPaste: true,
            autolink: true,
            defaultProtocol: 'https',
            HTMLAttributes: {
              target: '_blank',
              rel: 'noopener noreferrer nofollow',
              class: 'tiptap-link',
            },
          }),
          Placeholder.configure({
            placeholder: I18n.t('AgenticChatFlow.EditorPlaceholder'),
            showOnlyWhenEditable: true,
            emptyEditorClass: 'is-editor-empty',
            emptyNodeClass: 'is-empty',
          }),
          VariableHighlight,
          Table.configure({
            resizable: true,
            HTMLAttributes: { class: 'tiptap-table' },
            allowTableNodeSelection: true,
          }),
          TableRow,
          TableHeader,
          TableCell,
          Markdown.configure({
            html: false,
            tightLists: true,
            linkify: true,
            breaks: false,
            transformPastedText: true,
            transformCopiedText: true,
          }),
          MarkdownMention.configure({
            HTMLAttributes: { class: 'tiptap-mention' },
            renderText({ node }) {
              return `@${node.attrs.id}`;
            },
            renderHTML({ options, node }) {
              return [
                'span',
                { ...(options?.HTMLAttributes || {}), 'data-type': 'mention' },
                `@${node.attrs.id}`,
              ];
            },
            suggestion: {
              char: '@',
              items: ({ query }) => {
                const q = (query || '').toLowerCase();
                const catalog = referenceCatalogRef.current;
                if (!q) return catalog.slice(0, 8);
                return catalog
                  .filter(
                    r =>
                      r.name.toLowerCase().includes(q) ||
                      (r.description || '').toLowerCase().includes(q),
                  )
                  .slice(0, 8);
              },
              render: renderMentionSuggestion,
            },
          }),
          CollapsibleHeadings.configure({
            getCollapsedKeys: () => collapsedRef.current,
            onToggle: key => {
              setCollapsed(prev => {
                const next = new Set(prev);
                if (next.has(key)) next.delete(key);
                else next.add(key);
                return next;
              });
            },
          }),
          BlockScope.configure({
            enabled: () => viewChannelsRef.current,
            getLabel: modality =>
              I18n.t(
                modality === 'voice'
                  ? 'AgenticChatFlow.ScopeChipVoice'
                  : 'AgenticChatFlow.ScopeChipText',
              ),
            onToggle: (pos, modality) => {
              const ed = editorRef.current;
              if (!ed) return;
              const { state, view } = ed;
              const node = state.doc.nodeAt(pos);
              if (!node) return;
              const next = toggleScopeModality(node.attrs.scope, modality);
              const tr = state.tr.setNodeMarkup(pos, undefined, {
                ...node.attrs,
                scope: next,
              });
              view.dispatch(tr);
            },
          }),
        ],
        content: '',
        onUpdate: ({ editor: ed }) => {
          const md = ed.storage.markdown.getMarkdown();
          const scopes = collectScopesFromEditor(ed);
          setCurrentMarkdown(injectScopesIntoMarkdown(md, scopes));
        },
      },
      [renderMentionSuggestion],
    );

    editorRef.current = editor;

    const collapseAllSections = useCallback(ed => {
      if (!ed) return;
      const keys = collectCollapsibleHeadingKeys(ed.state.doc);
      const next = new Set(keys);
      collapsedRef.current = next;
      setCollapsed(next);
      const { state, view } = ed;
      view.dispatch(state.tr.setMeta(collapsedHeadingsKey, { recompute: true }));
    }, []);

    const load = useCallback(async () => {
      if (!entityId || !editor) return;
      const list = await AgentInstruction.list(entityType, entityId);
      setVersions(list);
      const active = list.find(v => v.active) || list[0];
      const initial = active ? blocksToText(active.blocks) : '';
      const { stripped, scopes } = extractScopesFromMarkdown(initial);
      editor.commands.setContent(stripped || '');
      applyScopesToEditor(editor, scopes);
      collapseAllSections(editor);
      setLoadedMarkdown(initial);
      setCurrentMarkdown(initial);
      setTimeout(() => {
        const names = new Set(referenceCatalogRef.current.map(r => r.name));
        decoratePlainMentions(editor, names);
      }, 0);
    }, [entityType, entityId, editor, collapseAllSections]);

    useEffect(() => {
      load();
    }, [load]);

    // When a heading is collapsed/expanded, force the decoration plugin to rebuild.
    useEffect(() => {
      if (!editor) return;
      const { state, view } = editor;
      view.dispatch(state.tr.setMeta(collapsedHeadingsKey, { recompute: true }));
    }, [editor, collapsed]);

    // When the View Channels toggle flips, force BlockScope decorations to rebuild.
    useEffect(() => {
      if (!editor) return;
      const { state, view } = editor;
      view.dispatch(state.tr.setMeta(blockScopeKey, { recompute: true }));
    }, [editor, viewChannels]);

    const mentions = useMemo(() => extractMentionsFromMarkdown(currentMarkdown), [currentMarkdown]);
    const unresolvedMentions = useMemo(
      () => mentions.filter(m => !referenceByName.has(m.name)),
      [mentions, referenceByName],
    );

    const dirty = currentMarkdown !== loadedMarkdown;

    useEffect(() => {
      onDirtyChange?.(dirty);
    }, [dirty, onDirtyChange]);

    const headingKeysInDoc = editor ? collectCollapsibleHeadingKeys(editor.state.doc) : [];

    const allCollapsed =
      headingKeysInDoc.length > 0 && headingKeysInDoc.every(k => collapsed.has(k));

    const handleToggleAll = () => {
      if (!editor) return;
      const keys = collectCollapsibleHeadingKeys(editor.state.doc);
      const all = keys.length > 0 && keys.every(k => collapsed.has(k));
      if (all) setCollapsed(new Set());
      else setCollapsed(new Set(keys));
    };

    const openSave = () => {
      setCommit('');
      setDialogOpen(true);
    };

    const performSave = async () => {
      setSaving(true);
      try {
        const created = await AgentInstruction.create({
          entityType,
          entityId,
          blocks: [{ type: 'textarea', text: currentMarkdown }],
          commit: commit.trim() || undefined,
        });
        let activated = created;
        if (created?._id) {
          activated = await AgentInstruction.activate(created._id);
        }
        setDialogOpen(false);
        setCommit('');
        await load();
        const version = activated?.version ?? created?.version;
        if (version != null) {
          await onSaved?.({ entityType, entityId, version });
        }
        return { entityType, entityId, version };
      } finally {
        setSaving(false);
      }
    };

    const activate = async id => {
      await AgentInstruction.activate(id);
      await load();
    };

    const discard = useCallback(() => {
      if (!editor) return;
      const { stripped, scopes } = extractScopesFromMarkdown(loadedMarkdown || '');
      editor.commands.setContent(stripped || '');
      applyScopesToEditor(editor, scopes);
      collapseAllSections(editor);
      setCurrentMarkdown(loadedMarkdown);
      setTimeout(() => {
        const names = new Set(referenceCatalogRef.current.map(r => r.name));
        decoratePlainMentions(editor, names);
      }, 0);
    }, [editor, loadedMarkdown, collapseAllSections]);

    useImperativeHandle(
      ref,
      () => ({
        isDirty: () => currentMarkdown !== loadedMarkdown,
        discard,
        openSaveDialog: () => {
          setCommit('');
          setDialogOpen(true);
        },
      }),
      [currentMarkdown, loadedMarkdown, discard],
    );

    const previewMarkdown = useMemo(
      () => filterMarkdownForPreview(currentMarkdown, previewModality),
      [currentMarkdown, previewModality],
    );
    const previewLines = useMemo(() => previewMarkdown.split('\n'), [previewMarkdown]);

    const tokenCount = Math.ceil(previewMarkdown.length / 4);
    const lineCount = previewLines.length;

    const copyMarkdown = async () => {
      try {
        await navigator.clipboard.writeText(previewMarkdown);
        setCopyTooltip(true);
        setTimeout(() => setCopyTooltip(false), 1400);
      } catch {
        // ignore
      }
    };

    const showTitle = title !== '' && title !== null && title !== undefined;
    const closeMenu = () => setMenuAnchor(null);
    const openHistory = () => {
      closeMenu();
      setHistoryOpen(true);
    };

    const exportMarkdownFile = () => {
      closeMenu();
      const blob = new Blob([currentMarkdown], { type: 'text/markdown;charset=utf-8' });
      const href = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = href;
      const base =
        (title || `${entityType}-instructions`)
          .toLowerCase()
          .replace(/[^a-z0-9]+/gi, '-')
          .replace(/^-+|-+$/g, '') || 'instructions';
      link.download = `${base}.md`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(href);
    };

    const openImportMarkdown = () => {
      closeMenu();
      importInputRef.current?.click();
    };

    const handleImportMarkdownFile = event => {
      const input = event.currentTarget;
      const file = input.files?.[0];
      if (input) input.value = '';
      if (!file || !editor) return;

      const reader = new FileReader();
      reader.onload = () => {
        if (typeof reader.result !== 'string') return;
        const { stripped, scopes } = extractScopesFromMarkdown(reader.result);
        editor.commands.setContent(stripped || '');
        applyScopesToEditor(editor, scopes);
        collapseAllSections(editor);
        setCurrentMarkdown(reader.result);
        setTimeout(() => {
          const names = new Set(referenceCatalogRef.current.map(r => r.name));
          decoratePlainMentions(editor, names);
        }, 0);
      };
      reader.onerror = () => {
        // ignore read failures
      };
      reader.readAsText(file);
    };

    return (
      <Box sx={styles.root}>
        <Box sx={styles.titleStrip}>
          <Stack
            direction="row"
            spacing={1.5}
            sx={{
              alignItems: 'center',
              minWidth: 0,
            }}
          >
            {showTitle && (
              <Typography sx={styles.headerTitle} noWrap>
                {title || I18n.t('AgenticChatFlow.InstructionTitle')}
              </Typography>
            )}
            {unresolvedMentions.length > 0 && (
              <Chip
                size="small"
                icon={<AlertOctagonOutlineIcon size={14} />}
                label={I18n.t('AgenticChatFlow.UnresolvedMentions', {
                  count: unresolvedMentions.length,
                })}
                sx={styles.mentionChipUnresolved}
              />
            )}
            {!parentManagedSave && dirty && (
              <Chip
                size="small"
                label={I18n.t('AgenticChatFlow.UnsavedChanges')}
                sx={styles.dirtyChip}
              />
            )}
          </Stack>
          <Stack
            direction="row"
            spacing={1}
            sx={{
              alignItems: 'center',
            }}
          >
            <Button
              variant="outlined"
              color="secondary"
              startIcon={
                <SvgIcon color="primary">
                  <EyeOutlineIcon />
                </SvgIcon>
              }
              size="small"
              onClick={() => setPreviewOpen(true)}
            >
              {I18n.t('AgenticChatFlow.Preview')}
            </Button>
            {!parentManagedSave && dirty && (
              <Button
                variant="text"
                size="small"
                onClick={discard}
                disabled={saving}
                sx={{ textTransform: 'none' }}
              >
                {I18n.t('AgenticChatFlow.Discard')}
              </Button>
            )}
            {!parentManagedSave && (
              <Button
                variant="contained"
                color="primary"
                size="small"
                onClick={openSave}
                disabled={saving || !currentMarkdown.trim() || !dirty}
              >
                {I18n.t('AgenticChatFlow.SaveVersion')}
              </Button>
            )}
            <Tooltip title={I18n.t('AgenticChatFlow.MoreActions')}>
              <Button
                sx={styles.iconButton}
                variant="outlined"
                color="secondary"
                size="small"
                onClick={e => setMenuAnchor(e.currentTarget)}
              >
                <DotsVerticalIcon size={18} />
              </Button>
            </Tooltip>
            <Menu
              anchorEl={menuAnchor}
              open={Boolean(menuAnchor)}
              onClose={closeMenu}
              anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
              transformOrigin={{ vertical: 'top', horizontal: 'right' }}
            >
              <MenuItem onClick={openHistory}>
                <HistoryIcon size={16} style={{ marginRight: 8 }} />
                {I18n.t('AgenticChatFlow.VersionHistory')}
              </MenuItem>
              <Divider />
              <MenuItem onClick={exportMarkdownFile}>
                <TrayArrowDownIcon size={16} style={{ marginRight: 8 }} />
                {I18n.t('AgenticChatFlow.Export')}
              </MenuItem>
              <MenuItem onClick={openImportMarkdown}>
                <TrayArrowUpIcon size={16} style={{ marginRight: 8 }} />
                {I18n.t('AgenticChatFlow.Import')}
              </MenuItem>
            </Menu>
            <input
              ref={importInputRef}
              type="file"
              accept=".md,.markdown,text/markdown,text/plain"
              hidden
              onChange={handleImportMarkdownFile}
            />
          </Stack>
        </Box>

        {helper && <Typography sx={styles.helper}>{helper}</Typography>}

        <Box sx={styles.toolbarStrip}>
          <Tooltip
            title={
              allCollapsed
                ? I18n.t('AgenticChatFlow.ExpandAll')
                : I18n.t('AgenticChatFlow.CollapseAll')
            }
          >
            <ButtonBase sx={styles.iconButton} onClick={handleToggleAll}>
              {allCollapsed ? (
                <UnfoldMoreHorizontalIcon size={14} />
              ) : (
                <UnfoldLessHorizontalIcon size={14} />
              )}
              {allCollapsed
                ? I18n.t('AgenticChatFlow.ExpandAll')
                : I18n.t('AgenticChatFlow.CollapseAll')}
            </ButtonBase>
          </Tooltip>
          <FormControlLabel
            sx={styles.channelsSwitchLabel}
            labelPlacement="start"
            control={
              <Switch size="small" checked={viewChannels} onChange={(_, v) => setViewChannels(v)} />
            }
            label={I18n.t('AgenticChatFlow.ViewChannels')}
          />
        </Box>

        <Box sx={styles.editorWrap}>
          {editor && (
            <BubbleMenu editor={editor} options={{ placement: 'top' }}>
              <Box sx={styles.bubble}>
                <Box
                  component="button"
                  type="button"
                  sx={styles.bubbleBtn}
                  data-active={editor.isActive('bold')}
                  onClick={() => editor.chain().focus().toggleBold().run()}
                  title="Bold"
                >
                  <FormatBoldIcon size={14} />
                </Box>
                <Box
                  component="button"
                  type="button"
                  sx={styles.bubbleBtn}
                  data-active={editor.isActive('italic')}
                  onClick={() => editor.chain().focus().toggleItalic().run()}
                  title="Italic"
                >
                  <FormatItalicIcon size={14} />
                </Box>
                <Box
                  component="button"
                  type="button"
                  sx={styles.bubbleBtn}
                  data-active={editor.isActive('heading', { level: 1 })}
                  onClick={() => editor.chain().focus().toggleHeading({ level: 1 }).run()}
                  title="Heading 1"
                >
                  <FormatHeader1Icon size={14} />
                </Box>
                <Box
                  component="button"
                  type="button"
                  sx={styles.bubbleBtn}
                  data-active={editor.isActive('heading', { level: 2 })}
                  onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}
                  title="Heading 2"
                >
                  <FormatHeader2Icon size={14} />
                </Box>
                <Box
                  component="button"
                  type="button"
                  sx={styles.bubbleBtn}
                  data-active={editor.isActive('heading', { level: 3 })}
                  onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()}
                  title="Heading 3"
                >
                  <FormatHeader3Icon size={14} />
                </Box>
                <Box
                  component="button"
                  type="button"
                  sx={styles.bubbleBtn}
                  data-active={editor.isActive('bulletList')}
                  onClick={() => editor.chain().focus().toggleBulletList().run()}
                  title="Bullet list"
                >
                  <FormatListBulletedIcon size={14} />
                </Box>
                <Box
                  component="button"
                  type="button"
                  sx={styles.bubbleBtn}
                  data-active={editor.isActive('orderedList')}
                  onClick={() => editor.chain().focus().toggleOrderedList().run()}
                  title="Numbered list"
                >
                  <FormatListNumberedIcon size={14} />
                </Box>
                <Box sx={styles.bubbleDivider} />
                <Box
                  component="button"
                  type="button"
                  sx={styles.bubbleBtn}
                  data-active={editor.isActive('code')}
                  onClick={() => editor.chain().focus().toggleCode().run()}
                  title={I18n.t('AgenticChatFlow.InlineCode')}
                >
                  <CodeTagsIcon size={14} />
                </Box>
                <Box
                  component="button"
                  type="button"
                  sx={styles.bubbleBtn}
                  data-active={editor.isActive('codeBlock')}
                  onClick={() => editor.chain().focus().toggleCodeBlock().run()}
                  title={I18n.t('AgenticChatFlow.CodeBlock')}
                >
                  <CodeBracesIcon size={14} />
                </Box>
                {!editor.isActive('table') && (
                  <Box
                    component="button"
                    type="button"
                    sx={styles.bubbleBtn}
                    onClick={() =>
                      editor
                        .chain()
                        .focus()
                        .insertTable({ rows: 3, cols: 3, withHeaderRow: true })
                        .run()
                    }
                    title={I18n.t('AgenticChatFlow.InsertTable')}
                  >
                    <TableIcon size={14} />
                  </Box>
                )}
                {editor.isActive('table') && (
                  <>
                    <Box sx={styles.bubbleDivider} />
                    <Box
                      component="button"
                      type="button"
                      sx={styles.bubbleBtn}
                      onClick={() => editor.chain().focus().addColumnAfter().run()}
                      title={I18n.t('AgenticChatFlow.AddColumn')}
                    >
                      <TableColumnPlusAfterIcon size={14} />
                    </Box>
                    <Box
                      component="button"
                      type="button"
                      sx={styles.bubbleBtn}
                      onClick={() => editor.chain().focus().deleteColumn().run()}
                      title={I18n.t('AgenticChatFlow.DeleteColumn')}
                    >
                      <TableColumnRemoveIcon size={14} />
                    </Box>
                    <Box
                      component="button"
                      type="button"
                      sx={styles.bubbleBtn}
                      onClick={() => editor.chain().focus().addRowAfter().run()}
                      title={I18n.t('AgenticChatFlow.AddRow')}
                    >
                      <TableRowPlusAfterIcon size={14} />
                    </Box>
                    <Box
                      component="button"
                      type="button"
                      sx={styles.bubbleBtn}
                      onClick={() => editor.chain().focus().deleteRow().run()}
                      title={I18n.t('AgenticChatFlow.DeleteRow')}
                    >
                      <TableRowRemoveIcon size={14} />
                    </Box>
                    <Box
                      component="button"
                      type="button"
                      sx={styles.bubbleBtn}
                      onClick={() => editor.chain().focus().deleteTable().run()}
                      title={I18n.t('AgenticChatFlow.DeleteTable')}
                    >
                      <TableRemoveIcon size={14} />
                    </Box>
                  </>
                )}
              </Box>
            </BubbleMenu>
          )}
          <EditorContent editor={editor} />
        </Box>

        {mentions.length > 0 && (
          <Box sx={styles.detected}>
            <Typography sx={styles.detectedLabel}>
              {I18n.t('AgenticChatFlow.DetectedReferences')}
            </Typography>
            {mentions.map(mention => {
              const known = referenceByName.has(mention.name);
              return (
                <Tooltip
                  key={mention.name}
                  title={
                    known
                      ? referenceByName.get(mention.name).description || ''
                      : I18n.t('AgenticChatFlow.UnresolvedMentionHelp')
                  }
                >
                  <Chip
                    size="small"
                    label={`@${mention.name}`}
                    sx={known ? styles.mentionChipResolved : styles.mentionChipUnresolved}
                  />
                </Tooltip>
              );
            })}
          </Box>
        )}

        <Dialog open={historyOpen} onClose={() => setHistoryOpen(false)} maxWidth="sm" fullWidth>
          <DialogTitle
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              fontSize: theme.typography.pxToRem(18),
              fontWeight: 600,
            }}
          >
            {I18n.t('AgenticChatFlow.VersionHistory')}
            <IconButton size="small" onClick={() => setHistoryOpen(false)}>
              <CloseIcon size={18} />
            </IconButton>
          </DialogTitle>
          <Divider />
          <DialogContent sx={{ p: 0 }}>
            <Box sx={styles.versionsDialogList}>
              {versions.length === 0 ? (
                <Typography sx={[styles.meta, { p: 2 }]}>
                  {I18n.t('AgenticChatFlow.NoVersions')}
                </Typography>
              ) : (
                versions.map(v => (
                  <Box key={v._id} sx={styles.versionRow}>
                    <Box sx={styles.versionMain}>
                      <Box sx={styles.versionTitleRow}>
                        <Typography sx={styles.versionTitle}>
                          {I18n.t('AgenticChatFlow.VersionLabel', { version: v.version })}
                        </Typography>
                        {v.active && (
                          <Typography sx={styles.currentTag}>
                            {`(${I18n.t('AgenticChatFlow.Active')})`}
                          </Typography>
                        )}
                      </Box>
                      <Typography sx={[styles.versionMeta, { mt: 0.25 }]}>
                        {formatDate(v.createdAt)}
                      </Typography>
                      {v.commit && (
                        <Typography sx={[styles.versionCommit, { mt: 0.5 }]}>
                          <Box component="span" sx={{ fontWeight: 600 }}>
                            {`${I18n.t('AgenticChatFlow.CommitMessage')}: `}
                          </Box>
                          {v.commit}
                        </Typography>
                      )}
                    </Box>
                    {!v.active && (
                      <Button
                        sx={styles.rollbackButton}
                        onClick={() => activate(v._id)}
                        startIcon={
                          <SvgIcon>
                            <HistoryIcon />
                          </SvgIcon>
                        }
                      >
                        {I18n.t('AgenticChatFlow.Rollback')}
                      </Button>
                    )}
                  </Box>
                ))
              )}
            </Box>
          </DialogContent>
        </Dialog>

        <Drawer
          anchor="right"
          open={previewOpen}
          onClose={() => setPreviewOpen(false)}
          sx={styles.previewDrawer}
        >
          <Box sx={styles.previewHeader}>
            <Stack
              direction="row"
              spacing={1}
              sx={{
                alignItems: 'center',
              }}
            >
              <Typography sx={styles.previewTitle}>
                {I18n.t('AgenticChatFlow.PreviewTitle')}
              </Typography>
              <Typography sx={styles.meta}>
                {I18n.t('AgenticChatFlow.LinesAndTokens', {
                  lines: lineCount,
                  tokens: tokenCount,
                })}
              </Typography>
            </Stack>
            <Stack
              direction="row"
              spacing={1}
              sx={{
                alignItems: 'center',
              }}
            >
              <Stack
                direction="row"
                spacing={0}
                sx={{
                  alignItems: 'center',
                  border: '1px solid',
                  borderColor: 'divider',
                  borderRadius: 1,
                  overflow: 'hidden',
                  mr: 1,
                }}
              >
                {MODALITIES.map(modality => {
                  const active = previewModality === modality;
                  return (
                    <button
                      key={modality}
                      type="button"
                      onClick={() => setPreviewModality(modality)}
                      style={{
                        appearance: 'none',
                        border: 0,
                        background: active ? theme.palette.mentionBack : 'transparent',
                        color: active ? theme.palette.mention : theme.palette.text.secondary,
                        fontWeight: active ? 600 : 500,
                        fontSize: 12,
                        padding: '4px 10px',
                        cursor: 'pointer',
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: 4,
                      }}
                      title={I18n.t(
                        modality === 'voice'
                          ? 'AgenticChatFlow.PreviewAsVoice'
                          : 'AgenticChatFlow.PreviewAsText',
                      )}
                    >
                      <ChannelModalityIcon modality={modality} size={13} />
                      {I18n.t(
                        modality === 'voice'
                          ? 'AgenticChatFlow.ScopeChipVoice'
                          : 'AgenticChatFlow.ScopeChipText',
                      )}
                    </button>
                  );
                })}
              </Stack>
              <Tooltip
                open={copyTooltip}
                title={I18n.t('AgenticChatFlow.Copied')}
                placement="bottom"
              >
                <Box component="button" type="button" sx={styles.iconButton} onClick={copyMarkdown}>
                  <ContentCopyIcon size={14} />
                  {I18n.t('AgenticChatFlow.Copy')}
                </Box>
              </Tooltip>
              <IconButton onClick={() => setPreviewOpen(false)} size="small">
                <CloseIcon size={18} />
              </IconButton>
            </Stack>
          </Box>
          <Box sx={styles.previewBody}>
            {previewLines.map((line, i) => renderPreviewLine(line, styles, `pl-${i}`))}
          </Box>
        </Drawer>

        <Dialog
          open={dialogOpen}
          onClose={() => !saving && setDialogOpen(false)}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle sx={{ fontSize: theme.typography.pxToRem(18), fontWeight: 600 }}>
            {I18n.t('AgenticChatFlow.SaveVersionTitle')}
          </DialogTitle>
          <DialogContent>
            <Typography sx={[styles.meta, { mb: 1.5 }]}>
              {I18n.t('AgenticChatFlow.SaveVersionHelper')}
            </Typography>
            {unresolvedMentions.length > 0 && (
              <Alert severity="warning" sx={{ mb: 2 }}>
                {I18n.t('AgenticChatFlow.UnresolvedMentionsWarning', {
                  names: unresolvedMentions.map(m => `@${m.name}`).join(', '),
                })}
              </Alert>
            )}
            <TextField
              autoFocus
              fullWidth
              size="small"
              label={I18n.t('AgenticChatFlow.CommitMessage')}
              placeholder={I18n.t('AgenticChatFlow.CommitMessagePlaceholder')}
              value={commit}
              onChange={e => setCommit(e.target.value)}
              onKeyDown={e => {
                if (e.key === 'Enter' && (commit.trim() || e.metaKey)) {
                  e.preventDefault();
                  performSave();
                }
              }}
            />
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2 }}>
            <Button onClick={() => setDialogOpen(false)} color="inherit" disabled={saving}>
              {I18n.t('AgenticChatFlow.Cancel')}
            </Button>
            <Button
              onClick={performSave}
              variant="contained"
              color="primary"
              disableElevation
              disabled={saving}
            >
              {I18n.t('AgenticChatFlow.SaveVersion')}
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    );
  },
);

// Defaults are declared via destructured arguments; the rule can't detect them through forwardRef.

InstructionEditor.propTypes = {
  entityType: PropTypes.string.isRequired,
  entityId: PropTypes.string,
  title: PropTypes.string,
  helper: PropTypes.string,
  availableTools: PropTypes.arrayOf(PropTypes.object),
  availableSkills: PropTypes.arrayOf(PropTypes.object),
  parentManagedSave: PropTypes.bool,
  onDirtyChange: PropTypes.func,
  onSaved: PropTypes.func,
};

export default InstructionEditor;
